import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import HttpService from "../httpServices";
import { useUserContext } from "../../context/UserContext";
import { routes } from "../apiroutes";
import useMutateItem from "../useMutateItem";
import { showSuccessAlert, showErrorAlert } from "../../utils/alert";
import { ErrorHandler } from "../errorHandler";

const httpService = new HttpService();

// Payroll Dashboard
export const useGetPayrollDashboard = () => {
  const { user } = useUserContext();
  
  return useQuery({
    queryKey: ["payrollDashboard", user?.company_id],
    queryFn: async () => {
      const response = await httpService.getData(routes.getPayrollDashboard());
      return response.data;
    },
    enabled: !!user?.company_id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Company Wallet Management (NEW!)
export const useGetCompanyWallet = () => {
  const { user } = useUserContext();
  
  return useQuery({
    queryKey: ["companyWallet", user?.company_id],
    queryFn: async () => {
      const response = await httpService.getData(routes.getCompanyWallet());
      return response.data;
    },
    enabled: !!user?.company_id && ['company_admin', 'ACCOUNTANT'].includes(user?.role),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useGetFundingPreview = () => {
  const { user } = useUserContext();
  
  return useQuery({
    queryKey: ["fundingPreview", user?.company_id],
    queryFn: async () => {
      const response = await httpService.getData(routes.getFundingPreview());
      return response.data;
    },
    enabled: !!user?.company_id && ['company_admin', 'ACCOUNTANT'].includes(user?.role),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useFundWallet = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ amount, currency }) => {
      const response = await httpService.postData({
        amount,
        currency
      }, routes.fundWallet());
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["companyWallet"]);
      queryClient.invalidateQueries(["fundingHistory"]);
    },
  });
};

export const useConfirmFunding = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ transaction_reference, amount, currency }) => {
      const response = await httpService.postData({
        transaction_reference,
        amount,
        currency
      }, routes.confirmFunding());
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["companyWallet"]);
      queryClient.invalidateQueries(["fundingHistory"]);
    },
  });
};

// Get payment status after Flutterwave redirect
export const useGetPaymentStatus = (txRef, options = {}) => {
  const { enabled = true } = options;
  
  return useQuery({
    queryKey: ["paymentStatus", txRef],
    queryFn: async () => {
      if (!txRef) {
        throw new Error("Transaction reference is required");
      }
      // Validate tx_ref format: WALLET-{wallet_id}-{timestamp}
      const txRefPattern = /^WALLET-[a-f0-9]+-[\d.]+$/;
      if (!txRefPattern.test(txRef)) {
        throw new Error("Invalid transaction reference format");
      }
      const response = await httpService.getData(routes.getPaymentStatus(txRef));
      return response.data;
    },
    enabled: enabled && !!txRef,
    retry: 2,
    staleTime: 0, // Always fetch fresh status
  });
};

// Verify and update funding (returns full fee breakdown)
export const useVerifyAndUpdateFunding = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ tx_ref }) => {
      if (!tx_ref) {
        throw new Error("Transaction reference is required");
      }
      // Validate tx_ref format
      const txRefPattern = /^WALLET-[a-f0-9]+-[\d.]+$/;
      if (!txRefPattern.test(tx_ref)) {
        throw new Error("Invalid transaction reference format");
      }
      const response = await httpService.postData({
        tx_ref
      }, routes.verifyAndUpdateFunding());
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["companyWallet"]);
      queryClient.invalidateQueries(["fundingHistory"]);
      queryClient.invalidateQueries(["paymentStatus"]);
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to verify payment";
      showErrorAlert(errorMessage);
    },
  });
};

export const useGetSettlementBreakdown = (params = {}) => {
  const { user } = useUserContext();
  
  return useQuery({
    queryKey: ["settlementBreakdown", user?.company_id, params.month, params.year],
    queryFn: async () => {
      const response = await httpService.getData(routes.getSettlementBreakdown(params));
      return response.data;
    },
    enabled: !!user?.company_id && ['company_admin', 'ACCOUNTANT'].includes(user?.role),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useGetFundingHistory = (params = {}) => {
  const { user } = useUserContext();
  
  return useQuery({
    queryKey: ["fundingHistory", user?.company_id, params],
    queryFn: async () => {
      const response = await httpService.getData(routes.getFundingHistory(params));
      return response.data;
    },
    enabled: !!user?.company_id && ['company_admin', 'ACCOUNTANT'].includes(user?.role),
  });
};

// Manual funding confirmation (backup method)
export const useConfirmFundingManual = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ transaction_reference, amount, currency }) => {
      const response = await httpService.postData({
        transaction_reference,
        amount,
        currency
      }, routes.confirmFunding());
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["companyWallet"]);
      queryClient.invalidateQueries(["fundingHistory"]);
    },
  });
};

// Get wallet status (alternative to company wallet endpoint)
export const useGetWalletStatus = () => {
  const { user } = useUserContext();
  
  return useQuery({
    queryKey: ["walletStatus", user?.company_id],
    queryFn: async () => {
      const response = await httpService.getData(routes.getWalletStatus());
      return response.data;
    },
    enabled: !!user?.company_id && ['company_admin', 'ACCOUNTANT'].includes(user?.role),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

// Employee Bank Details (Updated)
export const useGetEmployeeBankDetails = (employeeId) => {
  return useQuery({
    queryKey: ["employeeBankDetails", employeeId],
    queryFn: async () => {
      const response = await httpService.getData(routes.getEmployeeBankDetails({ employee_id: employeeId }));
      return response.data;
    },
    enabled: !!employeeId,
  });
};

export const useGetAllEmployeeBankDetails = (params = {}) => {
  return useQuery({
    queryKey: ["allEmployeeBankDetails", params],
    queryFn: async () => {
      const response = await httpService.getData(routes.getAllEmployeeBankDetails(params));
      return response;
    },
  });
};

export const useAddEmployeeBankDetails = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (bankDetails) => {
      const response = await httpService.postData(bankDetails, routes.addEmployeeBankDetails());
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["employeeBankDetails", variables.employee_id]);
      queryClient.invalidateQueries(["allEmployeeBankDetails"]);
      queryClient.invalidateQueries(["payrollDashboard"]);
    },
  });
};

export const useUpdateEmployeeBankDetails = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (bankDetails) => {
      const response = await httpService.putData(bankDetails, routes.updateEmployeeBankDetails());
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["employeeBankDetails", variables.employee_id]);
      queryClient.invalidateQueries(["allEmployeeBankDetails"]);
      queryClient.invalidateQueries(["payrollDashboard"]);
    },
  });
};

// Name Enquiry for Bank Account Verification
export const useNameEnquiry = () => {
  return useMutation({
    mutationFn: async ({ account_number, bank_code }) => {
      const response = await httpService.postData(
        { account_number, bank_code },
        routes.nameEnquiry()
      );
      return response.data;
    },
  });
};

// Batch Management
export const useGetPayrollBatches = (filters = {}) => {
  const { user } = useUserContext();
  
  return useQuery({
    queryKey: ["payrollBatches", user?.company_id, filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await httpService.getData(routes.getPayrollBatches(params));
      return response.data;
    },
    enabled: !!user?.company_id,
  });
};

export const useCreatePayrollBatch = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (batchData) => {
      const response = await httpService.postData(batchData, routes.createPayrollBatch());
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["payrollBatches"]);
      queryClient.invalidateQueries(["payrollDashboard"]);
    },
  });
};

export const useUpdatePayrollBatch = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ batchId, batchData }) => {
      const response = await httpService.putData(batchData, routes.updatePayrollBatch(batchId));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["payrollBatches"]);
      queryClient.invalidateQueries(["payrollDashboard"]);
    },
  });
};

export const useDeletePayrollBatch = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (batchId) => {
      const response = await httpService.deleteData(routes.deletePayrollBatch(batchId));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["payrollBatches"]);
      queryClient.invalidateQueries(["payrollDashboard"]);
    },
  });
};

// HR Approvals
export const useGetPendingHRApprovals = () => {
  const { user } = useUserContext();
  
  return useQuery({
    queryKey: ["pendingHRApprovals", user?.company_id],
    queryFn: async () => {
      const response = await httpService.getData(routes.getPendingHRApprovals());
      return response.data;
    },
    enabled: !!user?.company_id,
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
  });
};

export const useApproveHRBatch = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (batchId) => {
      const response = await httpService.postData({}, routes.approveHRBatch(batchId));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["pendingHRApprovals"]);
      queryClient.invalidateQueries(["pendingAccountantApprovals"]);
      queryClient.invalidateQueries(["payrollBatches"]);
      queryClient.invalidateQueries(["payrollDashboard"]);
    },
  });
};

export const useRejectHRBatch = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ batchId, rejection_reason }) => {
      const response = await httpService.postData({
        rejection_reason
      }, routes.rejectHRBatch(batchId));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["pendingHRApprovals"]);
      queryClient.invalidateQueries(["payrollBatches"]);
      queryClient.invalidateQueries(["payrollDashboard"]);
    },
  });
};

// Accountant Approvals
export const useGetPendingAccountantApprovals = () => {
  const { user } = useUserContext();
  
  return useQuery({
    queryKey: ["pendingAccountantApprovals", user?.company_id],
    queryFn: async () => {
      const response = await httpService.getData(routes.getPendingAccountantApprovals());
      return response.data;
    },
    enabled: !!user?.company_id,
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
  });
};

export const useApproveAccountantBatch = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ batchId, payment_method }) => {
      // Include payment_method in payload if provided (for transaction-time selection)
      const payload = payment_method ? { payment_method } : {};
      const response = await httpService.postData(payload, routes.approveAccountantBatch(batchId));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["pendingAccountantApprovals"]);
      queryClient.invalidateQueries(["payrollBatches"]);
      queryClient.invalidateQueries(["paymentTracking"]);
      queryClient.invalidateQueries(["payrollDashboard"]);
    },
  });
};

export const useRejectAccountantBatch = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ batchId, rejection_reason }) => {
      const response = await httpService.postData({
        rejection_reason
      }, routes.rejectAccountantBatch(batchId));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["pendingAccountantApprovals"]);
      queryClient.invalidateQueries(["payrollBatches"]);
      queryClient.invalidateQueries(["payrollDashboard"]);
    },
  });
};

// Payment Execution (NEW!)
export const useExecutePayrollPayment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (batchId) => {
      const response = await httpService.postData({}, routes.executePayrollPayment(batchId));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["payrollBatches"]);
      queryClient.invalidateQueries(["paymentTracking"]);
      queryClient.invalidateQueries(["payrollDashboard"]);
    },
  });
};

// Payment Tracking
export const useGetPaymentTracking = (filters = {}) => {
  const { user } = useUserContext();
  
  return useQuery({
    queryKey: ["paymentTracking", user?.company_id, filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await httpService.getData(routes.getPaymentTracking(params));
      return response.data;
    },
    enabled: !!user?.company_id,
    refetchInterval: 10 * 1000, // Refetch every 10 seconds for real-time updates
  });
};

export const useGetBatchStatus = (batchId, options = {}) => {
  return useQuery({
    queryKey: ["batchStatus", batchId],
    queryFn: async () => {
      const response = await httpService.getData(routes.getBatchStatus(batchId));
      return response.data;
    },
    enabled: !!batchId && (options.enabled !== false),
    refetchInterval: false, // No auto-refresh
    ...options
  });
};

export const useRetryFailedPayments = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (batchId) => {
      const response = await httpService.postData({}, routes.retryFailedPayments(batchId));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["paymentTracking"]);
      queryClient.invalidateQueries(["batchStatus"]);
      queryClient.invalidateQueries(["payrollDashboard"]);
    },
  });
};

// Payroll Reports (Enhanced)
export const useGetEnhancedPayrollReports = (filters = {}) => {
  const { user } = useUserContext();
  
  return useQuery({
    queryKey: ["enhancedPayrollReports", user?.company_id, filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await httpService.getData(routes.getEnhancedPayrollReports(params));
      return response.data;
    },
    enabled: !!user?.company_id,
  });
};

export const useExportPayrollReport = () => {
  return useMutation({
    mutationFn: async ({ filters, format = 'csv' }) => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      params.append('format', format);
      
      const response = await httpService.getData(routes.exportPayrollReport(params), {
        responseType: 'blob'
      });
      return response.data;
    },
  });
};

// Utility hooks
export const useGetEmployeesForPayroll = (filters = {}) => {
  const { user } = useUserContext();
  
  return useQuery({
    queryKey: ["employeesForPayroll", user?.company_id, filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await httpService.getData(routes.getEmployeesForPayroll(params));
      return response.data;
    },
    enabled: !!user?.company_id,
  });
};

export const useGetPayrollStatistics = (filters = {}) => {
  const { user } = useUserContext();
  
  return useQuery({
    queryKey: ["payrollStatistics", user?.company_id, filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await httpService.getData(routes.getPayrollStatistics(params));
      return response.data;
    },
    enabled: !!user?.company_id,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Payroll Deductions System
export const useGetPayrollDeductions = (filters = {}) => {
  const { user } = useUserContext();
  
  return useQuery({
    queryKey: ["payrollDeductions", user?.company_id, filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await httpService.getData(routes.getPayrollDeductions(params));
      return response;
    },
    enabled: !!user?.company_id,
  });
};

export const useCreatePayrollDeduction = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (payload) => httpService.postData(payload, routes.createPayrollDeduction()),
    onSuccess: (requestParams) => {
      const resData = requestParams || {};
      showSuccessAlert("Payroll deduction created successfully");
      if (handleSuccess) {
        handleSuccess(resData);
      }
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    createDeductionData: data,
    createDeductionError: ErrorHandler(error),
    createDeductionIsLoading: isPending,
    createDeductionPayload: (requestPayload) => mutate(requestPayload),
  };
};

export const useUpdatePayrollDeduction = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: ({ deductionId, payload }) => httpService.putData(payload, routes.updatePayrollDeduction(deductionId)),
    onSuccess: (requestParams) => {
      const resData = requestParams || {};
      showSuccessAlert("Payroll deduction updated successfully");
      if (handleSuccess) {
        handleSuccess(resData);
      }
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    updateDeductionData: data,
    updateDeductionError: ErrorHandler(error),
    updateDeductionIsLoading: isPending,
    updateDeductionPayload: (requestPayload) => mutate(requestPayload),
  };
};

export const useDeletePayrollDeduction = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (deductionId) => httpService.deleteData(routes.deletePayrollDeduction(deductionId)),
    onSuccess: (requestParams) => {
      const resData = requestParams || {};
      showSuccessAlert("Payroll deduction deleted successfully");
      if (handleSuccess) {
        handleSuccess(resData);
      }
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    deleteDeductionData: data,
    deleteDeductionError: ErrorHandler(error),
    deleteDeductionIsLoading: isPending,
    deleteDeductionPayload: (requestPayload) => mutate(requestPayload),
  };
};

// Payroll Allowances System
export const useGetPayrollAllowances = (params = {}) => {
  const { user } = useUserContext();
  
  return useQuery({
    queryKey: ["payrollAllowances", user?.company_id, params.page, params.limit, params.allowance_type, params.month, params.year, params.is_active],
    queryFn: async () => {
      const response = await httpService.getData(routes.getPayrollAllowances(params));
      return response.data;
    },
    enabled: !!user?.company_id,
  });
};

export const useGetPayrollAllowance = (allowanceId) => {
  const { user } = useUserContext();
  
  return useQuery({
    queryKey: ["payrollAllowance", user?.company_id, allowanceId],
    queryFn: async () => {
      const response = await httpService.getData(routes.getPayrollAllowance(allowanceId));
      return response.data;
    },
    enabled: !!user?.company_id && !!allowanceId,
  });
};

export const useCreatePayrollAllowance = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (payload) => httpService.postData(payload, routes.createPayrollAllowance()),
    onSuccess: (requestParams) => {
      const resData = requestParams || {};
      showSuccessAlert("Allowance created successfully");
      if (handleSuccess) {
        handleSuccess(resData);
      }
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    createAllowanceData: data,
    createAllowanceError: ErrorHandler(error),
    createAllowanceIsLoading: isPending,
    createAllowancePayload: (requestPayload) => mutate(requestPayload),
  };
};

export const useUpdatePayrollAllowance = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: ({ allowanceId, payload }) => httpService.putData(payload, routes.updatePayrollAllowance(allowanceId)),
    onSuccess: (requestParams) => {
      const resData = requestParams || {};
      showSuccessAlert("Allowance updated successfully");
      if (handleSuccess) {
        handleSuccess(resData);
      }
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    updateAllowanceData: data,
    updateAllowanceError: ErrorHandler(error),
    updateAllowanceIsLoading: isPending,
    updateAllowancePayload: (requestPayload) => mutate(requestPayload),
  };
};

export const useDeletePayrollAllowance = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (allowanceId) => httpService.deleteData(routes.deletePayrollAllowance(allowanceId)),
    onSuccess: (requestParams) => {
      const resData = requestParams || {};
      showSuccessAlert("Allowance deactivated successfully");
      if (handleSuccess) {
        handleSuccess(resData);
      }
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    deleteAllowanceData: data,
    deleteAllowanceError: ErrorHandler(error),
    deleteAllowanceIsLoading: isPending,
    deleteAllowancePayload: (requestPayload) => mutate(requestPayload),
  };
};

// Settlement Clearing System
export const useGetTaxAccount = () => {
  const { user } = useUserContext();
  
  return useQuery({
    queryKey: ["taxAccount", user?.company_id],
    queryFn: async () => {
      const response = await httpService.getData(routes.getTaxAccount());
      return response.data;
    },
    enabled: !!user?.company_id,
  });
};

export const useSetTaxAccount = () => {
  const queryClient = useQueryClient();
  const { user } = useUserContext();
  
  return useMutation({
    mutationFn: async (payload) => {
      const response = await httpService.postData(payload, routes.setTaxAccount());
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["taxAccount", user?.company_id]);
      queryClient.invalidateQueries(["settlementSummary", user?.company_id]);
    },
  });
};

export const useGetSettlementSummary = (params = {}) => {
  const { user } = useUserContext();
  
  return useQuery({
    queryKey: ["settlementSummary", user?.company_id, params.batch_id],
    queryFn: async () => {
      const response = await httpService.getData(routes.getSettlementSummary(params));
      return response.data;
    },
    enabled: !!user?.company_id,
    refetchInterval: false,
  });
};

export const usePaySettlementByCategory = () => {
  const queryClient = useQueryClient();
  const { user } = useUserContext();
  
  return useMutation({
    mutationFn: async (payload) => {
      const response = await httpService.postData(payload, routes.paySettlementByCategory());
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["settlementSummary", user?.company_id]);
      queryClient.invalidateQueries(["settlementPaymentHistory", user?.company_id]);
      queryClient.invalidateQueries(["companyWallet", user?.company_id]);
      queryClient.invalidateQueries(["settlementBreakdown", user?.company_id]);
    },
  });
};

export const usePayAllSettlements = () => {
  const queryClient = useQueryClient();
  const { user } = useUserContext();
  
  return useMutation({
    mutationFn: async () => {
      const response = await httpService.postData({}, routes.payAllSettlements());
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["settlementSummary", user?.company_id]);
      queryClient.invalidateQueries(["settlementPaymentHistory", user?.company_id]);
      queryClient.invalidateQueries(["companyWallet", user?.company_id]);
      queryClient.invalidateQueries(["settlementBreakdown", user?.company_id]);
    },
  });
};

export const useGetSettlementPaymentHistory = (params = {}) => {
  const { user } = useUserContext();
  
  return useQuery({
    queryKey: ["settlementPaymentHistory", user?.company_id, params.page, params.limit, params.category_name, params.payment_status],
    queryFn: async () => {
      const response = await httpService.getData(routes.getSettlementPaymentHistory(params));
      return response.data;
    },
    enabled: !!user?.company_id,
  });
};

// Funding Calculator
export const useCalculateFundingRequirement = (params = {}) => {
  const { user } = useUserContext();
  
  return useQuery({
    queryKey: ["fundingRequirement", user?.company_id, params],
    queryFn: async () => {
      const response = await httpService.getData(routes.calculateFundingRequirement(params));
      return response.data;
    },
    enabled: !!user?.company_id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Payment Flow Details
export const useGetPaymentFlowDetails = (batchId) => {
  const { user } = useUserContext();
  
  return useQuery({
    queryKey: ["paymentFlowDetails", user?.company_id, batchId],
    queryFn: async () => {
      const response = await httpService.getData(routes.getPaymentFlowDetails(batchId));
      return response.data;
    },
    enabled: !!user?.company_id && !!batchId,
  });
};

// Payroll Period Settings
export const useGetPayrollPeriodSettings = () => {
  const { user } = useUserContext();
  
  return useQuery({
    queryKey: ["payrollPeriodSettings", user?.company_id],
    queryFn: async () => {
      const response = await httpService.getData(routes.getPayrollPeriodSettings());
      return response.data;
    },
    enabled: !!user?.company_id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useUpdatePayrollPeriodSettings = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (payload) => httpService.putData(payload, routes.updatePayrollPeriodSettings()),
    onSuccess: (requestParams) => {
      const resData = requestParams || {};
      showSuccessAlert("Payroll period settings updated successfully");
      if (handleSuccess) {
        handleSuccess(resData);
      }
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
    queryKeys: ["payrollPeriodSettings"],
  });

  return {
    updatePayrollPeriodSettings: mutate,
    updatePayrollPeriodSettingsIsLoading: isPending,
    updatePayrollPeriodSettingsError: ErrorHandler(error),
    updatePayrollPeriodSettingsData: data?.data || data,
  };
};

// Direct Debit Hooks
export const useGetPaymentMethod = () => {
  const { user } = useUserContext();
  
  return useQuery({
    queryKey: ["paymentMethod", user?.company_id],
    queryFn: async () => {
      const response = await httpService.getData(routes.getPaymentMethod());
      return response.data;
    },
    enabled: !!user?.company_id,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useUpdatePaymentMethod = (handleSuccess) => {
  const queryClient = useQueryClient();
  
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (payload) =>
      httpService.putData(payload, routes.updatePaymentMethod()),
    onSuccess: (requestParams) => {
      const resData = requestParams?.data || {};
      showSuccessAlert(resData?.message || "Payment method updated successfully");
      queryClient.invalidateQueries(["paymentMethod"]);
      if (handleSuccess) handleSuccess(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
    queryKeys: ["paymentMethod"],
  });

  return {
    updatePaymentMethodData: data,
    updatePaymentMethodError: ErrorHandler(error),
    updatePaymentMethodIsLoading: isPending,
    updatePaymentMethodPayload: (requestPayload) => mutate(requestPayload),
  };
};

export const useGetMandateStatus = () => {
  const { user } = useUserContext();
  
  return useQuery({
    queryKey: ["mandateStatus", user?.company_id],
    queryFn: async () => {
      const response = await httpService.getData(routes.getMandateStatus());
      return response.data;
    },
    enabled: !!user?.company_id,
    staleTime: 0, // Always fetch fresh data to detect status changes
    refetchOnMount: true, // Refetch when component mounts
  });
};

export const useSetupDirectDebit = (handleSuccess) => {
  const queryClient = useQueryClient();
  
  const mutation = useMutateItem({
    mutationFn: (payload) =>
      httpService.postData(payload, routes.setupDirectDebit()),
    onSuccess: (requestParams) => {
      const resData = requestParams?.data || {};
      queryClient.invalidateQueries(["mandateStatus"]);
      queryClient.invalidateQueries(["paymentMethod"]);
      if (handleSuccess) handleSuccess(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
    queryKeys: ["mandateStatus", "paymentMethod"],
  });

  return {
    setupDirectDebitData: mutation.data,
    setupDirectDebitError: ErrorHandler(mutation.error),
    setupDirectDebitIsLoading: mutation.isPending,
    setupDirectDebitPayload: (requestPayload) => mutation.mutateAsync(requestPayload),
  };
};